import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Toaster } from "react-hot-toast";
import i18n from "./i18n/index.js";
import { useAppDispatch } from "./hooks/redux/useRedux";
import {
  getLocationThunk,
  getUserViewThunk,
} from "./redux/slices/profileSlice/asyncthunk";
import Verification from "./Auth/Verification/Verification";
import ForgotPassword from "./Auth/ForgotPassword/ForgotPassword";
import PasswordSuccess from "./Auth/PasswordSuccess/PasswordSuccess";
import AccountTypeSelect from "./Auth/AccountTypeSelect/AccountTypeSelect";
import Biography from "./Auth/Biography/Biography";
import ChooseNiche from "./Auth/ChooseNiche/ChooseNiche";
import SubNicheSelect from "./Auth/SubNicheSelect/SubNicheSelect";
import SuggestedServices from "./Auth/SuggestedServices/SuggestedServices";
import MyServices from "./Auth/MyServices/MyServices";
import Service from "./Service/Service";
import AppRedirect from "./Pages/appRedirect/AppRedirect";
import WorkingDaysPage from "./Auth/WorkingDays/WorkingDays";
import BookingSettingsPage from "./Auth/BookingSettingsPage/BookingSettingsPage";
import HowDidYouFind from "./Auth/HowDidYouFind/HowDidYouFind";
import FinishRegistration from "./Auth/FinishRegistration/FinishRegistration";
import CreatePassword from "./Auth/CreatePassword/createPassword";
import LayoutDashboard from "./Layout/Layout";
import ProtectedRoute from "./ProtectedRoute";
import PublicOnlyRoute from "./Auth/AuthHooks/PublicOnlyRoute";
import OnboardingRoute from "./Auth/AuthHooks/OnboardingRoute";
import VerificationPassword from "./Auth/VerificationPassword/VerificationPassword";
import ResetPassword from "./Auth/ResetPassword/ResetPassword";
import BiographySalon from "./Auth/BiographySalon/BiographySalon";
import BiographyMembers from "./Auth/BiographyMembers/BiographyMembers";
import OutboundRedirect from "./OutboundRedirect/OutboundRedirect";
import Worker from "./Auth/Worker/WorkerInvite";
import GoogleCallback from "./Auth/SignIn/GoogleCallback";
import ConnectedSuccessfully from "./Pages/Settings/ConnectedSuccessfully";
import { SubscriptionPaymentSuccess } from "./Pages/Settings/subscription/SubscriptionPaymentSuccess";
import { SubscriptionPaymentFailed } from "./Pages/Settings/subscription/SubscriptionPaymentFailed";
import PaymentPlansPage from "./Pages/Settings/subscription/PaymentPlansPage";
import AppleCallback from "./Auth/SignIn/AppleCallback";
import { getAccessToken, looksLikeJwt } from "./Auth/AuthHooks/authRouting";
import { initGA, trackPageview } from "./analytics";
import { getOnboardingPassCheck } from "./api/profile/profileServices";
import { getLanguageFromCountry } from "./utils/location/getLanguageFromCountry";

const SignIn = lazy(() => import("./Auth/SignIn/SignIn"));
const Register = lazy(() => import("./Auth/Register/Register"));
const HalperAnalytics = lazy(
  () => import("./Auth/HalperAnalytics/HalperAnalytics"),
);
const JIVO_WIDGET_ID = "8WSNoaqSgJ";
const JIVO_SCRIPT_SRC = `https://code.jivosite.com/widget/${JIVO_WIDGET_ID}`;
const JIVO_HIDDEN_PATHS = new Set([
  "/sign-in",
  "/register",
  "/forgot-password",
]);

const setJivoVisibility = (hidden: boolean) => {
  if (typeof document === "undefined") return;

  const jivoNodes = document.querySelectorAll(
    "jdiv, iframe[src*='jivosite.com'], [class*='jivo'], [id*='jivo']",
  );

  jivoNodes.forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    node.style.display = hidden ? "none" : "";
    node.setAttribute("aria-hidden", hidden ? "true" : "false");
  });
};

function IndexRedirect() {
  const token = getAccessToken();
  return (
    <Navigate to={looksLikeJwt(token) ? "/profile" : "/sign-in"} replace />
  );
}

const appleAppSiteAssociationData = {
  applinks: {
    details: [
      {
        appIDs: ["Q5736F8RVM.com.halper.ai"],
        components: [{ "/": "/*" }],
      },
    ],
  },
};

const assetLinksData = [
  {
    relation: ["delegate_permission/common.handle_all_urls"],
    target: {
      namespace: "android_app",
      package_name: "com.halper.ai",
      sha256_cert_fingerprints: [
        "07:67:BE:B9:F5:C0:4A:3C:F3:1B:FF:77:CA:C8:2B:C2:F1:47:3B:15:B3:98:71:AD:23:D9:0A:C9:90:D3:70:59",
      ],
    },
  },
];

function WellKnownView({ content }: { content: object }) {
  return (
    <pre
      style={{
        margin: 0,
        padding: 16,
        fontFamily: "monospace",
        whiteSpace: "pre-wrap",
        wordBreak: "break-all",
        backgroundColor: "#fff",
        color: "#000",
        minHeight: "100vh",
      }}
    >
      {JSON.stringify(content, null, 2)}
    </pre>
  );
}

function checkLanguages(countryCode: string) {
  const language = getLanguageFromCountry(countryCode);
  i18n.changeLanguage(language);
}

function App() {
  const dispatch = useAppDispatch();
  const location = useLocation();

  useEffect(() => {
    const token = getAccessToken();

    if (token) {
      getOnboardingPassCheck()
        .then((res) => {
          dispatch(getUserViewThunk())
            .unwrap()
            .then((res) => {
              if (res?.user?.primary_language?.name) {
                i18n.changeLanguage(res.user.primary_language.name);
              }
            })
            .catch(() => {});
        })
        .catch((err) => {
          const rawStatus =
            err.response?.data?.status ??
            err.response?.data?.code ??
            err.response?.data?.onboarding_code ??
            err.response?.status;

          const statusCode = parseInt(
            Array.isArray(rawStatus) ? rawStatus[0] : rawStatus,
            10,
          );

          if ([212, 224, 234].includes(statusCode)) {
            dispatch(getLocationThunk())
              .unwrap()
              .then((res) => {
                if (res?.country_code) {
                  checkLanguages(res.country_code);
                }
              })
              .catch(() => {});
          } else {
            dispatch(getUserViewThunk())
              .unwrap()
              .then((res) => {
                if (res?.user?.primary_language?.name) {
                  i18n.changeLanguage(res.user.primary_language.name);
                } else {
                  dispatch(getLocationThunk())
                    .unwrap()
                    .then((locRes) => {
                      if (locRes?.country_code) {
                        checkLanguages(locRes.country_code);
                      }
                    })
                    .catch(() => {});
                }
              })
              .catch(() => {
                dispatch(getLocationThunk())
                  .unwrap()
                  .then((locRes) => {
                    if (locRes?.country_code) {
                      checkLanguages(locRes.country_code);
                    }
                  })
                  .catch(() => {});
              });
          }
        });
    } else {
      dispatch(getLocationThunk())
        .unwrap()
        .then((res) => {
          if (res?.country_code) {
            checkLanguages(res.country_code);
          }
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    initGA();
  }, []);

  useEffect(() => {
    const fullPath = `${location.pathname}${location.search}${location.hash}`;
    trackPageview(fullPath);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const isWellKnown = location.pathname.startsWith("/.well-known");
    const shouldHideJivo = JIVO_HIDDEN_PATHS.has(location.pathname) || isWellKnown;
    const existingScript = document.querySelector(
      `script[data-jivo-widget="${JIVO_WIDGET_ID}"]`,
    );

    if (shouldHideJivo) {
      setJivoVisibility(true);
      return;
    }

    if (!existingScript) {
      const script = document.createElement("script");
      script.src = JIVO_SCRIPT_SRC;
      script.async = true;
      script.dataset.jivoWidget = JIVO_WIDGET_ID;
      script.onload = () => setJivoVisibility(false);
      document.body.appendChild(script);
    } else {
      setJivoVisibility(false);
    }
  }, [location.pathname]);

  return (
    <>
      <Helmet>
        <meta
          name="keywords"
          content="Halper AI, Halper business manager, Halper appointment automation, AI business manager, AI assistant for small business, Virtual business assistant, AI appointment scheduling, Business automation software, AI chatbot for business, AI CRM for solopreneurs, Automated booking system, Client communication automation, AI scheduling assistant"
        />
        <meta name="author" content="HalperAI" />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Halper AI: AI Business Manager for Solopreneurs and SMBs."
        />
        <meta
          property="og:description"
          content="Sign in to Halper AI and manage your business appointments, payments, and client chat from one dashboard. The AI manager built for solopreneurs and SMBs."
        />
        <meta property="og:url" content="https://app.halper.ai/" />
        <meta
          property="og:image"
          content="https://storage.googleapis.com/halper-website-bucket/main/media/images/blog_page/link.png"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Halper AI: AI Business Manager for Solopreneurs and SMBs."
        />
        <meta
          name="twitter:description"
          content="Sign in to Halper AI and manage your business appointments, payments, and client chat from one dashboard. The AI manager built for solopreneurs and SMBs."
        />
        <meta
          name="twitter:image"
          content="https://storage.googleapis.com/halper-website-bucket/main/media/images/blog_page/link.png"
        />
        <meta name="twitter:url" content="https://app.halper.ai/" />
      </Helmet>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: "white",
            color: "#1f2937",
            borderRadius: "12px",
            fontSize: "14px",
          },
          success: {
            duration: 3000,
          },
          error: {
            duration: 5000,
          },
        }}
      />

      <Routes>
        <Route
          path="/.well-known/apple-app-site-association"
          element={<WellKnownView content={appleAppSiteAssociationData} />}
        />
         
        <Route
          path="/.well-known/assetlinks.json"
          element={<WellKnownView content={assetLinksData} />}
        />

        <Route path="/app" element={<AppRedirect />} />

        <Route path="/" element={<IndexRedirect />} />
        <Route
          path="/halper-analytics"
          element={
            <ProtectedRoute>
              <Suspense fallback={null}>
                <HalperAnalytics />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sign-in"
          element={
            <PublicOnlyRoute>
              <Suspense fallback={null}>
                <SignIn />
              </Suspense>
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicOnlyRoute>
              <Suspense fallback={null}>
                <Register />
              </Suspense>
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/verify"
          element={
            <PublicOnlyRoute>
              <Verification />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/create-password"
          element={
            <PublicOnlyRoute>
              <CreatePassword />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/verification-password"
          element={
            <PublicOnlyRoute>
              <VerificationPassword />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicOnlyRoute>
              <ResetPassword />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicOnlyRoute>
              <ForgotPassword />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/password-reset-success"
          element={
            <PublicOnlyRoute>
              <PasswordSuccess />
            </PublicOnlyRoute>
          }
        />

        <Route
          path="/account-type-select"
          element={
            <OnboardingRoute>
              <AccountTypeSelect />
            </OnboardingRoute>
          }
        />
        <Route
          path="/biography"
          element={
            <OnboardingRoute>
              <Biography />
            </OnboardingRoute>
          }
        />
        <Route
          path="/biography-individual"
          element={
            <OnboardingRoute>
              <Biography />
            </OnboardingRoute>
          }
        />
        <Route
          path="/biography-salon"
          element={
            <OnboardingRoute>
              <BiographySalon />
            </OnboardingRoute>
          }
        />
        <Route
          path="/biography-member"
          element={
            <OnboardingRoute>
              <BiographyMembers />
            </OnboardingRoute>
          }
        />
        <Route
          path="/worker-biography"
          element={
            <OnboardingRoute>
              <Worker />
            </OnboardingRoute>
          }
        />

        <Route
          path="/choose-niche"
          element={
            <OnboardingRoute>
              <ChooseNiche />
            </OnboardingRoute>
          }
        />
        <Route
          path="/subniche-select"
          element={
            <OnboardingRoute>
              <SubNicheSelect />
            </OnboardingRoute>
          }
        />
        <Route
          path="/suggested-services"
          element={
            <OnboardingRoute>
              <SuggestedServices />
            </OnboardingRoute>
          }
        />
        <Route
          path="/my-services"
          element={
            <OnboardingRoute>
              <MyServices />
            </OnboardingRoute>
          }
        />
        <Route
          path="/services"
          element={
            <OnboardingRoute>
              <Service />
            </OnboardingRoute>
          }
        />
        <Route
          path="/working-days"
          element={
            <OnboardingRoute>
              <WorkingDaysPage />
            </OnboardingRoute>
          }
        />
        <Route
          path="/booking-settings"
          element={
            <OnboardingRoute>
              <BookingSettingsPage />
            </OnboardingRoute>
          }
        />
        <Route
          path="/how-did-you-find"
          element={
            <OnboardingRoute>
              <HowDidYouFind />
            </OnboardingRoute>
          }
        />
        <Route
          path="/finish-registration"
          element={
            <OnboardingRoute>
              <FinishRegistration />
            </OnboardingRoute>
          }
        />

        <Route path="/app/outbound/uae-1" element={<OutboundRedirect />} />
        <Route path="/worker-invite" element={<Worker />} />
        <Route path="/google-login" element={<GoogleCallback />} />
        <Route path="/apple-login" element={<AppleCallback />} />
        <Route
          path="/subscription-payment-success"
          element={<SubscriptionPaymentSuccess />}
        />
        <Route
          path="/subscription-payment-failed"
          element={<SubscriptionPaymentFailed />}
        />

        <Route
          path="/payment-plans"
          element={
            <ProtectedRoute>
              <PaymentPlansPage />
            </ProtectedRoute>
          }
        />

        <Route
          element={
            <ProtectedRoute>
              <LayoutDashboard />
            </ProtectedRoute>
          }
        >
          <Route path="/profile" />
          <Route path="/calendar" />
          <Route path="/clients" />
          <Route path="/excluded-contacts" />
          <Route path="/your-business" />
          <Route path="/chat" />
          <Route path="/analytics" />
          <Route path="/invoices" />
          <Route path="/payments" />
          <Route path="/marketplace" />
          <Route path="/accounting" />
          <Route path="/settings" />
          <Route path="/affiliate" />
        </Route>

        <Route
          path="/instagram-connect"
          element={
            <ProtectedRoute>
              <ConnectedSuccessfully />
            </ProtectedRoute>
          }
        />
        <Route
          path="/whatsapp-connect"
          element={
            <ProtectedRoute>
              <ConnectedSuccessfully />
            </ProtectedRoute>
          }
        />
        <Route
          path="/google-calendar-connect"
          element={
            <ProtectedRoute>
              <ConnectedSuccessfully />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default App;
